<template>
    <div class="canvas-map-flying" ref="canvasMap">
        <!-- <transition name="fade-cover">
            <div
                class="cover-text-flying"
                v-show="scrollyParams.progress(true) < 0.05"
            >
                <div class="cover-headline-container">
                    <h1 class="cover-headline" v-markdown="attributes.value.headline"></h1>
                </div>
                <div class="cover-deck" v-markdown="attributes.value.deck"></div>
                <div class="cover-byline" v-markdown="attributes.value.byline"></div>
            </div>
        </transition> -->
        <transition name="fade-labels">
            <svg
                ref="svg"
                :width="width"
                :height="height"
                v-show="showLabels"
            >
                <text
                    v-for="(label, i) in labels"
                    :key="i"
                    :x="label.x"
                    :y="label.y"
                    :dx="label.dxAdd"
                    :dy="label.dyAdd"
                    :text-anchor="label.anchor"
                    :class="label.class"
                >{{ label.name }}</text>
            </svg>
        </transition>
    </div>
</template>

<script>
import * as PIXI from "pixi.js"
import * as d3 from "d3"
import * as topojson from "topojson-client"
import BezierEasing from "bezier-easing"
import {gsap} from "gsap"
import {MotionPathPlugin} from "gsap/MotionPathPlugin.js"
import {PixiPlugin} from "gsap/PixiPlugin.js"
gsap.registerPlugin(MotionPathPlugin, PixiPlugin)
PixiPlugin.registerPIXI(PIXI) // give the plugin a reference to the PIXI object

import {mapState} from "vuex"
import polygons from "../../../../assets/data/countries-full.json"

export default {
    props: ["scrollyParams", "attributes"],
    
    data() {
        return {
            // Create an Application — PIXI area to display images in which a Canvas is created
            app: new PIXI.Application({
                width: 800,
                height: 600,
                backgroundColor: 0xE6EDEE,
                resolution: 1
            }),
            polygons: topojson.feature(polygons, polygons.objects["countries"]),
            width: 800,
            height: 600,
            translateX: 0,
            translateY: 0,

            translateXLabels: 0,
            translateYLabels: 0,

            myScale: null,
            labelsScale: null,
            countriesTexture: null,
            SINData: {
                country: "Singapore",
                city: "Singapore",
                airportCode: "SIN",
                lon: 103.987,
                lat: 1.356100
            },
            citiesToShow: [
                {name: "Singapore", lon: 103.987, lat: 1.356, class: "city", show: false},
                {name: "Jakarta", lon: 106.656, lat: -6.126, class: "city", show: true},
                {name: "Kuala Lumpur", lon: 101.71, lat: 2.746, class: "city", show: true},
                {name: "Manila", lon: 121.52, lat: 14.509, class: "city", show: true},
                {name: "Bangkok", lon: 100.747, lat: 13.681, class: "city", show: true},
                {name: "Ho Chi Minh City", lon: 106.652, lat: 10.819, class: "city", show: true},
                {name: "India", lon: 78.319, lat: 18.360, class: "country", show: true},
                {name: "Australia", lon: 132.577, lat: -17.174, class: "country", show: true}
            ],
            currentSlide: -1,
            currentYear: 2019,
            zoomOut: 13,
            zoomIn: 11,
            zoomDuration: 600,
            planesContainer: null,
            globeContainer: null,
            masterContainer: null,
            labels: [],
            showLabels: false,
        }
    },

    watch: {
        scrollyParams: function(newVal, oldVal) {
            if (newVal.slideIndex === oldVal.slideIndex) return

            this.currentSlide = newVal.slideIndex

            const direction = newVal.slideIndex > oldVal.slideIndex ? "down" : "up"
            const years = [2019, 2020, 2021]
            
            if (this.currentSlide === 2 && direction === "down") {
                const oldZoom = this.zoomIn
                const newZoom = this.zoomOut

                this.planesContainer.children[oldVal.slideIndex - 1].visible = false
                this.animateZoom(oldZoom, newZoom, oldVal.slideIndex - 1, newVal.slideIndex - 1)

                return
            }
            
            if (this.currentSlide === 1 && direction === "up") {
                const oldZoom = this.zoomOut
                const newZoom = this.zoomIn

                this.currentYear = years[0]

                this.planesContainer.children[oldVal.slideIndex - 1].visible = false
                
                // Next two rows — an urgent bug fixing, when while scrolling up fast the last layer with planes didn't hide
                this.planesContainer.children[2].visible = false
                this.planesContainer.children[3].visible = false

                this.animateZoom(oldZoom, newZoom, oldVal.slideIndex - 1, newVal.slideIndex - 1)

                return
            }
            
            if (this.currentSlide > 1 && this.currentSlide <= (years.length + 1) && oldVal.slideIndex <= (years.length + 1)) {
                this.currentYear = years[this.currentSlide - 1]
             
                this.planesContainer.children[newVal.slideIndex - 1].visible = true
                this.planesContainer.children[oldVal.slideIndex - 1].visible = false
            }
        }
    },

    created() {
        window.addEventListener("resize", this.onResize)
    },

    mounted() {
        this.myScale = this.calcScale(this.zoomOut) // set max zoom to make a texture in high resolution
        this.labelsScale = this.calcScale(this.zoomOut)
        this.width = window.screen.availWidth // availWidth property returns the width available for Web content on the screen
        if (global.isMobile) { // check whether is a mobile device
            this.height = window.screen.availHeight // This can be no larger than the value of window.screen.height, and will be less if the device or user agent reserves any vertical space for itself
        } else {
            this.height = window.innerHeight
        }
        this.translateX = this.width / 2
        this.translateY = this.height / 2

        if (window.innerWidth < 768) {
            this.zoomIn = 10
        }
        if (window.innerWidth > 1680) {
            this.zoomIn = 12
        }

        // Calculate separate values for labels to maintain their correct position while changing screen width
        this.translateXLabels = this.width / 2
        // this.translateYLabels = this.height / 2

        this.createLabelsData(this.citiesToShow)
        this.makeGlobeTexture()
        this.myScale = this.calcScale(this.zoomIn) // set zoom for initial view

        this.$el.appendChild(this.app.view) // Add the canvas that Pixi automatically created to the HTML document
        this.masterContainer = new PIXI.Container() // despite the stage is a PIXI Container itself, it's impossible to set the sortableChildren parameter of it to true to appennd zIndex to its children, that's why there is a need in creation an internal container — masterContainer in my case
        this.masterContainer.sortableChildren = true // allow container's children to get a zIndex and be sorted by it
        this.app.stage.addChild(this.masterContainer) // place masterContainer to Stage
        this.app.renderer.autoDensity = true // Resizes renderer view in CSS pixels to allow for resolutions other than 1
        this.app.renderer.resize(this.width, this.height)
        
        this.drawGlobe()
        this.fetchData()
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.onResize)
    },

    computed: {
        projection() {
            // return d3.geoMercator()
            // return d3.geoEqualEarth()
            return d3.geoOrthographic()
                // .center([ 103.81815, 1.36823 ])
                .rotate([ -103.987, -1.356 ])
                .scale(this.myScale)
                .translate([this.translateX, this.translateY])
        },

        adjustingProjection() { // to show countries shapes correctly, there was a little shift from real position during rendering
            return d3.geoOrthographic()
                .rotate([ -104.800, -1.800 ])
                .scale(this.myScale)
                .translate([this.translateX, this.translateY])
        },

        projectionLabels() {
            return d3.geoOrthographic()
                .rotate([ -103.987, -1.356 ])
                .scale(this.labelsScale)
                .translate([this.translateXLabels, this.translateY])
        },
        
        // Get long form header data from VueX
        ...mapState(["header"])
    },

    methods: {
        onResize() {
            this.width = window.screen.availWidth
            if (global.isMobile) {
                this.height = window.screen.availHeight
            } else {
                this.height = window.innerHeight
            }
            this.translateXLabels = this.width / 2
            // this.translateYLabels = this.height / 2
            this.createLabelsData(this.citiesToShow)
        },

        createLabelsData(data) {
            let labels = []
            for (let i=0; i<data.length; i++) {
                let d = data[i]

                let anchor, dxAdd, dyAdd, name
                if (d.name === "Kuala Lumpur") {
                    anchor = "end"
                    dxAdd = -4
                    dyAdd = 4
                    name = d.name
                } else if (d.name === "India" || d.name === "Australia") {
                    anchor = "middle"
                    dxAdd = 0
                    dyAdd = 0
                    name = d.name.toUpperCase()
                } else {
                    anchor = "start"
                    dxAdd = 4
                    dyAdd = 4
                    name = d.name
                }

                if (d.show) {
                    let proj = this.projectionLabels([d.lon, d.lat])
                    let dataPiece = {
                        name,
                        x: proj[0],
                        y: proj[1],
                        dxAdd,
                        dyAdd,
                        class: d.class,
                        anchor
                    }
                    labels.push(dataPiece)
                }
            }
            this.labels = labels
        },

        // Get JSON data from long form header
        fetchData() {
            // Choose a server depends of the project stage: development or production
            let dataUrl = process.env.NODE_ENV === "production"
                ? "https://www.straitstimes.com/multimedia/graphics/json/"
                : "https://st-graphics-dev-json.s3-ap-southeast-1.amazonaws.com/"
            dataUrl += this.header.airportsdata // Don't forget to change the property name here (given in the long form)
            fetch(dataUrl)
                .then(response => response.json())
                .then(data => {
                    let pathsData = []
                    for (let i = 0; i < data.length; i++) {
                        let d = data[i]
                        let proj = this.projection([d.lon, d.lat])
                        d.x = proj[0]
                        d.y = proj[1]
                        // Make an array with data for paths
                        pathsData.push(
                            {
                                "type": "Feature",
                                "properties":
                                    {"city": d.city, "airportCode": d.airportCode, "flights": d.flights, "year": d.year},
                                "geometry":
                                    {"type": "LineString", "coordinates": [
                                        [this.SINData.lon, this.SINData.lat],
                                        [d.lon, d.lat]
                                    ]}
                            }
                        )
                    }
                    this.animatePlanes(pathsData)
                    this.planesContainer.children[0].visible = true
                })
        },

        animatePlanes(data) {
            this.planesContainer = new PIXI.Container()
            this.planesContainer.zIndex = 1

            let velocity = 50 // speed in px per sec for all the planes
            let minDist = 15 // minimal distance between two planes for the busiest route in pixels

            let maxFrequency = d3.max(data.map(d => d.properties.flights))

            let containers = []
            let zoomLevels = [this.zoomIn, this.zoomOut]

            for (let n = 0; n < zoomLevels.length; n++) { // create containers for all the combinations zoom/year needed
                let uniqueYears = [...new Set(data.map(d => d.properties.year))]
                if (zoomLevels[n] == this.zoomIn) {
                    uniqueYears = uniqueYears.slice(0, 1) // for the smaller zoom take only first year
                }
                this.myScale = this.calcScale(zoomLevels[n])
                for (let m = 0; m < uniqueYears.length; m++) {
                    let yearContainer = new PIXI.Container()
                    yearContainer.visible = false

                    // let filteredData = data.filter(d => d.properties.year == this.currentYear)
                    let filteredData = data.filter(d => d.properties.year == uniqueYears[m])

                    let minStagger = minDist / velocity // min time in seconds between two planes

                    for (let i = 0; i < filteredData.length; i++) {
                        let currentFrequency = filteredData[i].properties.flights // get flights frequency for the current route
                        let currentStagger = maxFrequency * minStagger / currentFrequency // find current stagger — time between two aircrafts on the route relative to minStagger

                        // Draw path
                        let path = new PIXI.Graphics()
                            path.lineStyle(1, 0xff0000, 0)
                            this.pathGenerator(filteredData[i].geometry, path, this.projection)
                            path.endFill()

                            yearContainer.addChild(path)
                            // this.flightsApp.stage.addChild(path)

                        let points = path.geometry.graphicsData[0].shape.points // Get path's nodes a list of coordinates
                        // Convert list to an array of pairs of path's nodes coordinates
                        let values = []
                        for (let i = 0; i < points.length; i += 2) {
                            values.push({x: points[i], y: points[i+1]})
                        }
                        // Convert an array of coordinates to GSAP Raw Path format (need for MotionPathPlugin.getLength method)
                        let rawPath = MotionPathPlugin.arrayToRawPath(values)

                        let pathLength = MotionPathPlugin.getLength(rawPath) // Find path's length to set the same speed for all the planes
                        let planesDistance = currentStagger * velocity // distance between planes on this route in px
                        let count = (pathLength >= planesDistance ? Math.ceil(pathLength / planesDistance) : 1) // number of planes on the path at the same time
                        let planes = []
                        for (let j = 0; j < count; j++) { // make a set of planes presenting on the route at the same time
                            
                            let plane
                            switch (uniqueYears[m]) {
                                case 2019:
                                    plane = PIXI.Sprite.from(require("images/sample-images/airplane_icon_s_19.png"))
                                    break
                                case 2020:
                                    plane = PIXI.Sprite.from(require("images/sample-images/airplane_icon_s_20.png"))
                                    break
                                case 2021:
                                    plane = PIXI.Sprite.from(require("images/sample-images/airplane_icon_s_21.png"))
                                    break
                            }
                            // let plane = PIXI.Sprite.from(require("images/sample-images/airplane_icon_s.png"))
                            plane.anchor.set(0.5) // Set anchor to center
                            planes.push(plane)

                            yearContainer.addChild(plane)
                        }

                        for (let k = 0; k < planes.length; k++) {
                            // make a separate timeline for every plane. it allows to start a flight before the last plane in the row has arrived
                            // initially I tried to combine them as tweens in one timeline, but it's hard to overlap timelines
                            let tl = gsap.timeline({
                                // delay: currentStagger * k, // set delay to start planes one by one on the route (no need anymore coz progress set)
                                repeat: -1, // repeat endlessly
                                repeatDelay: (planesDistance * (planes.length) - pathLength) / velocity // set delay before repeat to make distances between planes equal
                            })

                            let appTime = 0.5 // set time in seconds during which any plane appears and dissapears

                            tl.fromTo(planes[k],
                                {
                                    pixi: {
                                        scale: 0,
                                        autoAlpha: 1
                                    },
                                },
                                {
                                    pixi: {
                                        scale: 1
                                    },
                                    duration: appTime
                                }
                            )

                            tl.to(planes[k], {
                                motionPath: {
                                    path: values,
                                    curviness: 0,
                                    fromCurrent: false,
                                    autoRotate: true,
                                    useRadians: true // Don't forget it, otherwize the Sprite doesn't follow the path correctly
                                },
                                duration: pathLength / velocity, // set the same speed for all the planes regardless path's length
                                ease: "none"
                            }, `>-${appTime}`) // set an overlap over previous tween coz it (previous) itself lasts appTime

                            tl.to(planes[k], {
                                pixi: {
                                    scale: 0
                                },
                                duration: appTime
                            }, `>-${appTime}`)

                            tl.to(planes[k], {
                                pixi: {
                                    autoAlpha: 0
                                }, 
                                duration: 0
                            })

                            // tl.progress(1 - (k / planes.length))
                            tl.progress((pathLength - planesDistance * k) / pathLength) // advance the animation to one cycle to have all the planes loaded
                        }
                    }
                    containers.push(yearContainer)
                }
            }
            this.planesContainer.addChild(...containers)
            this.masterContainer.addChild(this.planesContainer)
        },

        animateZoom(oldZoom, newZoom, oldIndex, newIndex) {
            let startTime = null
            let self = this
            if (newZoom < oldZoom) { // hide labels while zooming out
                self.showLabels = false
            }
            let easing = BezierEasing(.12,0,.39,0) // Create the easing function based on https://easings.net/en#easeInSine

            function animate(timestamp) {
                // The first time we're running rAF, we need to set the anchor point as to which we're going to calculate our duration
                if (!startTime) {
                    startTime = timestamp || new Date().getTime() //if browser doesn't support requestAnimationFrame, generate our own timestamp using Date
                }

                let timestamp_ = timestamp || new Date().getTime()
                // How long have we been animating in total?
                let runtime = timestamp_ - startTime
                // How much has our animation progressed relative to our duration goal? 
                // The result is a number (float) between 0 and 1. So 0 is zero percent en 1 is one hundred percent
                let relativeProgress = runtime / self.zoomDuration

                // We transform our relative progress to something else based on the easing that we used
                let easedProgress = easing(relativeProgress)

                let calculatedZoom = oldZoom + easedProgress * (newZoom - oldZoom)
                if (newZoom > oldZoom) { // check the direction of scrolling, then check whether it reaches the edge of the scale to keep the scale uniformed
                    calculatedZoom < newZoom ? self.myScale = self.calcScale(calculatedZoom) : self.myScale = self.calcScale(newZoom)
                } else {
                    calculatedZoom > newZoom ? self.myScale = self.calcScale(calculatedZoom) : self.myScale = self.calcScale(newZoom)
                }
                // self.myScale = self.calcScale(calculatedZoom)
                self.masterContainer.removeChild(self.globeContainer) // clear globe container at every frame while animating
                self.drawGlobe() // redraw the globe 60 fps

                // We want to request another frame when our desired duration isn't met yet
                if (runtime < self.zoomDuration) {
                    requestAnimationFrame(animate)
                } else {
                    if (self.currentSlide < 3) {
                        self.planesContainer.children[newIndex].visible = true
                        self.planesContainer.children[oldIndex].visible = false
                    }

                    // add points for the cities only when scrolling in
                    if (self.currentSlide === 2) {
                        let citiesCoord = []
                        self.citiesToShow.filter(city => city.class === "city").forEach(city => {
                            citiesCoord.push([city.lon, city.lat])
                        })

                        let points = {
                            type: "MultiPoint",
                            coordinates: citiesCoord
                        }

                        let citiesCircles = new PIXI.Graphics()
                        citiesCircles.beginFill(0x676767, 1)
                        self.pathGenerator(points, citiesCircles, self.projection)
                        citiesCircles.endFill()

                        self.globeContainer.addChild(citiesCircles)

                        self.showLabels = true // show labels while zooming in
                    }
                }
            }
            requestAnimationFrame(animate)
        },

        makeGlobeTexture() {
            let countries = new PIXI.Graphics()
            countries.beginFill(0xEFF3F4, 1)
            countries.lineStyle(1, 0xffffff, 1)
            this.pathGenerator({type: "FeatureCollection", features: this.polygons.features}, countries, this.adjustingProjection)
            countries.endFill()
            // countries.cacheAsBitmap = true

            this.countriesTexture = this.app.renderer.generateTexture(countries)
        },

        drawGlobe() {
            let globeShape = new PIXI.Graphics()
            globeShape.beginFill(0xffffff, 1)
            // globeShape.lineStyle(1, 0xcccccc, 1)
            this.pathGenerator({type: "Sphere"}, globeShape, this.projection)
            globeShape.endFill()

            let countriesSprite = new PIXI.Sprite(this.countriesTexture)
            countriesSprite.width = globeShape.width
            countriesSprite.height = globeShape.height
            countriesSprite.anchor.set(0.5) // Set anchor to center
            countriesSprite.position.set(this.translateX, this.translateY)

            let globeShadow = PIXI.Sprite.from(require("images/sample-images/shadow_globe.png"))
            globeShadow.width = globeShape.width
            globeShadow.height = globeShape.height
            globeShadow.anchor.set(0.5) // Set anchor to center
            globeShadow.position.set(this.translateX, this.translateY)

            this.globeContainer = new PIXI.Container()
            this.globeContainer.zindex = 0
            this.globeContainer.addChild(globeShape, countriesSprite, globeShadow)
            
            this.masterContainer.addChild(this.globeContainer)
        },

        pathGenerator(geometry, context, projection) {
            let pathGenerator = d3.geoPath()
                .projection(projection)
                .context(context) // Call methods on the specified context to render geometry (if non specified — svg). Change context for any PIXI.Graphics()
                .pointRadius(1.5) // Set points radius
            return pathGenerator(geometry)
        },

        calcScale(zoom) {
            return Math.pow(2,zoom) / (2 * Math.PI)
        }
    }
}
</script>

<style lang="scss">

    .canvas-map-flying {
        background-color: #E6EDEE;
        height: 100%;
        display: flex;
        justify-content: center;

        canvas {
        position: absolute;
        pointer-events: none;
    }

        svg {
            position: absolute;
            z-index: 1;
            text {
                font-family: $font-CuratorRegular;
                font-size: 14px;
                &.city {
                    fill: #676767;
                }
                &.country {
                    fill: #989898;
                }
            }
        }
    }


    .fade-labels-enter-active, .fade-labels-leave-active{
        transition: opacity 100ms;
    }

    .fade-cover-enter-active, .fade-cover-leave-active{
        transition: opacity 250ms;
    }

    .fade-labels-enter, .fade-labels-leave-to, .fade-cover-enter, .fade-cover-leave-to {
        opacity: 0;
    }

    // .cover-text-flying {
    //     z-index: 1;
    //     align-self: center;
    //     padding: 0 32px;
    //     max-width: 700px;
    //     // @media (min-width: 1440px) {
    //     //     max-width: 1440px;
    //     // }
    //     .cover-headline-container {
    //         padding: 25px 0 42px;
    //         @media (max-width: 768px) {
    //             padding: 12px 0 35px;
    //         }
    //         .cover-headline {
    //             font-family: $font-SelaneTwenty, Georgia, serif;
    //             font-size: 50px;
    //             line-height: 52px;
    //             margin: 0;
    //             @media (max-width: 768px) {
    //                 font-family: $font-SelaneForty;
    //                 font-size: 32px;
    //                 line-height: 35px;
    //             }
    //         }
    //     }
    //     .cover-deck {
    //         color: #494949;
    //         font-size: 23px;
    //         line-height: 30px;
    //         padding-bottom: 12px;
    //         @media (max-width: 768px) {
    //             font-size: 20px;
    //             line-height: 28px;
    //         }
    //     }
    //     .cover-byline {
    //         font-family: $font-CuratorRegular, "Helvetica Neue", Helvetica, Arial, sans-serif;
    //         color: #616161;
    //         text-transform: uppercase;
    //         font-size: 12px;
    //         line-height: 14.4px;
    //     }
    // }
</style>